import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';

// Efeito parallax para o fundo
const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('/images/hero-bg.jpg') no-repeat center center/cover;
  will-change: transform;
  transition: transform 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.3) 0%,
      transparent 100%
    );
  }
`;

// Overlay mais elegante com gradiente angular
const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg, 
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.6) 50%,
    rgba(0, 0, 0, 0.75) 100%
  );
  backdrop-filter: blur(3px);
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at 70% 30%,
      rgba(212, 175, 55, 0.15) 0%,
      transparent 70%
    );
    z-index: -1;
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
  text-align: center;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeroTitle = styled.h1.attrs({
  as: motion.h1
})`
  font-size: 4.5rem;
  margin-bottom: 1.5rem;
  color: var(--color-text);
  line-height: 1.2;
  max-width: 900px;
  text-align: center;
  letter-spacing: -0.5px;
  font-weight: 800;
  margin-left: auto;
  margin-right: auto;
  
  span {
    color: var(--color-accent);
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -5px;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, var(--color-accent) 0%, rgba(212, 175, 55, 0.6) 100%);
      transform: scaleX(0);
      transform-origin: left;
      animation: underlineAnimation 1.5s ease-out forwards 1s;
      border-radius: 2px;
      
      @keyframes underlineAnimation {
        to { transform: scaleX(1); }
      }
    }
  }
  
  @media (max-width: 992px) {
    font-size: 3.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p.attrs({
  as: motion.p
})`
  font-size: 1.3rem;
  margin-bottom: 3rem;
  color: var(--color-text-secondary);
  max-width: 800px;
  text-align: center;
  line-height: 1.8;
  font-weight: 400;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ButtonContainer = styled.div.attrs({
  as: motion.div
})`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const PrimaryButton = styled.a.attrs({
  as: Link
})`
  display: inline-block;
  background: linear-gradient(135deg, var(--color-accent) 0%, rgba(212, 175, 55, 0.85) 100%);
  color: var(--color-primary);
  padding: 1.1rem 2.6rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.85) 0%, var(--color-accent) 100%);
    z-index: -1;
    transition: opacity 0.3s ease;
    opacity: 0;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(212, 175, 55, 0.4);
    
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
  }
`;

const SecondaryButton = styled.a.attrs({
  as: Link
})`
  display: inline-block;
  background-color: transparent;
  color: var(--color-text);
  padding: 1rem 2.5rem;
  border: 2px solid var(--color-text);
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--color-text);
    color: var(--color-primary);
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
  }
`;

const ScrollIndicator = styled.div.attrs({
  as: motion.div
})`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  
  @media (max-width: 768px) {
    bottom: 1.5rem;
  }
`;

const ScrollText = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const ScrollIcon = styled.div.attrs({
  as: motion.div
})`
  width: 32px;
  height: 52px;
  border: 2px solid var(--color-accent);
  border-radius: 25px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    width: 6px;
    height: 6px;
    background-color: var(--color-accent);
    border-radius: 50%;
    transform: translateX(-50%);
    animation: scrollAnimation 2s cubic-bezier(0.645, 0.045, 0.355, 1) infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.7) 0%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  @keyframes scrollAnimation {
    0% { top: 10px; opacity: 1; }
    70% { opacity: 0; }
    100% { top: 35px; opacity: 0; }
  }
`;

const Hero: React.FC = () => {
  const [offsetY, setOffsetY] = useState(0);
  
  // Efeito parallax ao rolar
  useEffect(() => {
    const handleScroll = () => {
      // Limitar o efeito parallax para evitar movimento excessivo
      const newOffset = Math.min(window.pageYOffset, 500);
      setOffsetY(newOffset);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Variantes de animação para o título com texto sequencial
  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.8,
        delay: 0.3 + (i * 0.1) 
      }
    })
  };

  // Variantes para as palavras do título
  const titleWords = [
    { text: "Ternos em Vila Velha", isHighlighted: true },
    { text: "com Elegância e Sofisticação", isHighlighted: false }
  ];

  return (
    <HeroSection id="hero">
      <HeroBackground style={{ transform: `translateY(${offsetY * 0.2}px)` }} />
      <HeroOverlay />
      <HeroContent>
        <HeroTitle>
          {titleWords.map((word, i) => (
            <motion.span
              key={i}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={titleVariants}
              style={{ 
                display: 'inline-block',
                color: word.isHighlighted ? 'var(--color-accent)' : 'var(--color-text)',
                position: 'relative'
              }}
            >
              {word.text}
              {word.isHighlighted && (
                <motion.span
                  style={{
                    position: 'absolute',
                    bottom: '-5px',
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, var(--color-accent) 0%, rgba(212, 175, 55, 0.6) 100%)',
                    borderRadius: '2px'
                  }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.5, delay: 1, ease: "easeOut" }}
                />
              )}
              {i === 0 ? ' ' : ''}
            </motion.span>
          ))}
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Aluguel de <strong>ternos premium em Vila Velha</strong> para noivos, padrinhos, formaturas e eventos especiais. 
          Oferecemos <strong>ternos italianos</strong>, <strong>smoking</strong>, <strong>terno slim</strong> e <strong>trajes completos</strong> com condições especiais para grupos.
        </HeroSubtitle>
        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <PrimaryButton
            to="products"
            smooth={true}
            duration={800}
            spy={true}
            exact="true"
            offset={-80}
          >
            Aluguel de Ternos
          </PrimaryButton>
          <SecondaryButton
            to="contact"
            smooth={true}
            duration={800}
            spy={true}
            exact="true"
            offset={-80}
          >
            Agendar Prova
          </SecondaryButton>
        </ButtonContainer>
      </HeroContent>
      
      <ScrollIndicator
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <ScrollText>Explore</ScrollText>
        <ScrollIcon
          animate={{ 
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "loop"
          }}
        />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero; 